import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getIntelReport } from '../api/intel';
import { parseIntelData } from '../types/intel';
import { formatNumber } from '../utils/format';
import { getRaceDisplayName, formatRulerWithTitle } from '../constants/game';
import { Spinner } from './Spinner';
import { ErrorMessage } from './ErrorMessage';
import { COLORS } from '../constants/theme';
import { SectionHeader } from './ui/SectionHeader';
import type { IntelReportDto } from '../types/intel';

interface CrystalBallResultProps {
  intelReportId: string;
  runesConsumed: number;
  manaConsumed: number;
}

export function CrystalBallResult({ intelReportId, runesConsumed, manaConsumed }: CrystalBallResultProps) {
  const { worldId } = useAuth();
  const [intel, setIntel] = useState<IntelReportDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchIntel() {
      if (!worldId) return;
      try {
        setIsLoading(true);
        const data = await getIntelReport(worldId, intelReportId);
        setIntel(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load intel');
      } finally {
        setIsLoading(false);
      }
    }

    fetchIntel();
  }, [worldId, intelReportId]);

  if (isLoading) {
    return (
      <div
        className="border p-6"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <div className="flex items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !intel) {
    return (
      <div
        className="border p-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <ErrorMessage message={error || 'Intel not found'} />
      </div>
    );
  }

  const parsedIntel = parseIntelData(intel);
  if (parsedIntel.type !== 'CB') {
    return (
      <div
        className="border p-4"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        <ErrorMessage message="Invalid intel type" />
      </div>
    );
  }

  const data = parsedIntel.data;

  // Format ruler name with personality title and honor title
  const formattedRuler = formatRulerWithTitle(data.rulerName, data.personality, data.title);

  // Left column data - matches ThroneRoom layout (no Personality row, honor is in ruler name)
  const leftColumn = [
    { label: 'Race', value: getRaceDisplayName(data.race) },
    { label: 'Ruler', value: formattedRuler },
    { label: 'Land', value: `${formatNumber(data.totalLand)} Acres` },
    { label: 'Peasants', value: formatNumber(data.peasants) },
    { label: 'Building Eff.', value: `${Math.round(data.buildingEfficiency * 100)}%` },
    { label: 'Money', value: `${formatNumber(data.money)}gc` },
    { label: 'Food', value: `${formatNumber(data.food)} bushels` },
    { label: 'Runes', value: `${formatNumber(data.runes)} runes` },
    { label: 'Trade Balance', value: `${formatNumber(data.tradeBalance)}gc` },
  ];

  // Right column data - Military (thieves and wizards hidden)
  const rightColumn = [
    { label: 'Soldiers', value: formatNumber(data.soldiers) },
    { label: 'Off Specialists', value: formatNumber(data.offenseSpecialists) },
    { label: 'Def Specialists', value: formatNumber(data.defenseSpecialists) },
    { label: 'Elites', value: formatNumber(data.elites) },
    { label: 'Thieves', value: 'HIDDEN', hidden: true },
    { label: 'Wizards', value: 'HIDDEN', hidden: true },
    { label: 'War Horses', value: formatNumber(data.warHorses) },
    { label: 'Prisoners', value: formatNumber(data.prisoners) },
    { label: 'Off. Points', value: formatNumber(data.offensePoints) },
    { label: 'Def. Points', value: formatNumber(data.defensePoints) },
  ];

  return (
    <div className="space-y-4">
      {/* Success message - green banner */}
      <div
        className="border px-4 py-3"
        style={{
          backgroundColor: COLORS.successBg,
          borderColor: COLORS.successBorder,
          color: COLORS.successText,
        }}
      >
        Your wizards gather their runes and begin casting. The spell consumes{' '}
        <span className="text-yellow-300 font-semibold">{formatNumber(runesConsumed)} Runes</span>
        {manaConsumed > 0 && (
          <> and <span className="text-blue-300 font-semibold">{Math.round(manaConsumed)}% mana</span></>
        )}
        ... The spell is successful!
      </div>

      {/* Province info table */}
      <div
        className="border"
        style={{ backgroundColor: COLORS.card, borderColor: COLORS.border }}
      >
        {/* Province Header */}
        <SectionHeader
          title={`Our crystal ball reveals ${intel.targetProvinceName} (${intel.targetKingdomLocation})`}
        />

        {/* Two-column table — grid-cols-1 below lg: (1024px), matching the page's own
            APP_SHELL_BREAKPOINT/useAppShellPlatform() switch. This component renders below the
            shell switch regardless of which shell is active, so a narrower breakpoint would put
            a two-column table on screen alongside the single-column mobile shell. */}
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Column */}
          <div className="border-b lg:border-b-0 lg:border-r" style={{ borderColor: COLORS.border }}>
            <div
              className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide"
              style={{ color: COLORS.text.tertiary, backgroundColor: COLORS.card }}
            >
              Province
            </div>
            {leftColumn.map((row, idx) => (
              <div
                key={row.label}
                className="flex justify-between px-4 py-1.5 text-sm"
                style={{ backgroundColor: idx % 2 === 0 ? COLORS.card : COLORS.tableRowAlt }}
              >
                <span style={{ color: COLORS.text.secondary }}>{row.label}</span>
                <span className="font-medium" style={{ color: COLORS.text.primary }}>{row.value}</span>
              </div>
            ))}
          </div>

          {/* Right Column — zebra index offset by leftColumn.length so the stripe continues
              correctly when the columns stack below lg: instead of restarting at idx 0. */}
          <div>
            <div
              className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide"
              style={{ color: COLORS.text.tertiary, backgroundColor: COLORS.card }}
            >
              Military
            </div>
            {rightColumn.map((row, idx) => (
              <div
                key={row.label}
                className="flex justify-between px-4 py-1.5 text-sm"
                style={{ backgroundColor: (idx + leftColumn.length) % 2 === 0 ? COLORS.card : COLORS.tableRowAlt }}
              >
                <span style={{ color: COLORS.text.secondary }}>{row.label}</span>
                <span
                  className={row.hidden ? 'italic' : 'font-medium'}
                  style={{ color: row.hidden ? COLORS.text.muted : COLORS.text.primary }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Active Effects */}
        {data.activeEffects.length > 0 && (
          <div className="px-4 py-2" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <span className="text-sm" style={{ color: COLORS.text.secondary }}>Active Effects: </span>
            <span className="text-sm" style={{ color: COLORS.text.primary }}>
              {data.activeEffects.map((effect, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  {effect.effectType} ({effect.indefinite ? 'active' : `${effect.ticksRemaining} days`})
                </span>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* View in Intel Hub link */}
      <div>
        <Link
          to="/intel"
          className="text-sm hover:underline"
          style={{ color: COLORS.link }}
        >
          View in Intel Hub
        </Link>
      </div>
    </div>
  );
}
